import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import { KYCBanner } from '@atlas/components';
import {
  btag,
  pushToDataLayer,
  StorageManager,
  useApplicationState,
  useVerificationCheck,
} from '@atlas/core';
import { Button, Icon, Label } from '@atlas/ui';

import { KYC_AUTO_RUN_KEY } from 'common/constants';
import { throttle } from 'common/utils/throttle';
import { defaultRoutes } from 'components/MainRouter/constants';

const VERIFY_THROTTLE_MS = 2000;

const Verification = () => {
  const applicationState = useApplicationState();
  const { account } = applicationState;
  const { clientId, docNumber, firstName, lastName } = account;

  const { verificationRequired, verify } = useVerificationCheck({
    docNumber,
    firstName,
    lastName,
    onInit: () => {
      pushToDataLayer('KYC_started', { client: { id: clientId } }, btag);
    },
    onSuccess: () => {
      pushToDataLayer('KYC_completed', { client: { id: clientId } }, btag);
    },
  });

  const verifyRef = useRef(verify);
  verifyRef.current = verify;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledVerify = useCallback(
    throttle(() => verifyRef.current(), VERIFY_THROTTLE_MS),
    [],
  );

  useEffect(() => {
    if (StorageManager.getItem(KYC_AUTO_RUN_KEY)) {
      StorageManager.removeItem(KYC_AUTO_RUN_KEY);
      throttledVerify();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return createPortal(
    <div className="profile-wrapper-absolute">
      <div className="verification-page">
        <Link className="back-to-container" to={defaultRoutes.account}>
          <Icon className="icon-back" name="back" />
          <Label className="title-text" message="kyc_verification" />
        </Link>
        <div className="verification-content-wrapper">
          {verificationRequired ? (
            <KYCBanner
              button={
                <Button
                  className="kyc-verification__button"
                  label="complete_verification"
                  onClick={throttledVerify}
                  size="big"
                  type="primary"
                />
              }
              className="kyc-banner"
              description={
                <Label
                  className="kyc-verification__description"
                  message="kyc_verification_description"
                />
              }
              icon={
                <Icon
                  className="kyc-verification__icon  icon-red"
                  height={32}
                  name="error-legitimuz"
                  width={32}
                />
              }
              layout="vertical"
              title={<Label className="kyc-verification__title" message="kyc_verification_title" />}
            />
          ) : (
            <KYCBanner
              className="kyc-banner"
              description={
                <Label
                  className="kyc-verification__description"
                  message="success_verification_description"
                />
              }
              icon={
                <Icon
                  className="kyc-verification__icon icon-green"
                  height={32}
                  name="success"
                  width={32}
                />
              }
              layout="vertical"
              title={
                <Label className="kyc-verification__title" message="success_verification_title" />
              }
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Verification;
